/**
 * Locale configuration.
 *
 * Deliberately *not* a `/[locale]/...` routing scheme. The whole product is
 * built on `fuse.mn/{username}` — a path prefix would put a reserved segment in
 * front of every handle, make `fuse.mn/en/bat` and `fuse.mn/bat` two spellings
 * of one page, and force every shared link to carry a language the recipient
 * did not pick. The URL stays the identity; the language rides alongside it.
 *
 * The cost of that choice is stated in `resolveLocale()`.
 */

export const LOCALES = ['mn', 'en', 'ru', 'es'] as const

export type Locale = (typeof LOCALES)[number]

/**
 * Fallback of last resort, used only when the host is unknown and the browser
 * asked for nothing we speak. Mongolian is the primary market.
 */
export const DEFAULT_LOCALE: Locale = 'mn'

/**
 * Cookie holding the choice for this device. Readable during SSR, which is why
 * it — and not localStorage — is what the server reads. See `resolveLocale()`.
 *
 * Not `httpOnly`: the switcher writes it from the client so the change applies
 * without a round trip, and there is nothing secret in a language name.
 */
export const LOCALE_COOKIE = 'fuse_locale'

/** A year. The choice is a preference, not a session. */
export const LOCALE_COOKIE_MAX_AGE = 60 * 60 * 24 * 365

/** Mirror of the cookie, used to restore it if it is cleared. See LocaleProvider. */
export const LOCALE_STORAGE_KEY = 'fuse.locale'

/**
 * Query parameter that forces a language for one request: `?lang=en`.
 *
 * It outranks everything, including the cookie and the domain policy, because
 * it is the most explicit request a visitor can make. Two things need it:
 * someone who lands on fuse.mn from abroad and cannot read the page well enough
 * to find the switcher, and any automated reviewer fetching the site with no
 * cookie to its name — Google's OAuth brand review among them, which reads the
 * home page to decide whether it explains what the app does.
 */
export const LOCALE_QUERY = 'lang'

/**
 * Request header the middleware uses to carry a valid `?lang=` through to the
 * server components. A header rather than re-reading the URL, because the root
 * layout and `generateMetadata` resolve the locale too, and all three have to
 * agree or the page renders English text under `<html lang="mn">`.
 */
export const LOCALE_HEADER = 'x-fuse-locale'

export interface LocaleMeta {
  /** Endonym — a language is listed the way its own speakers write it. */
  label: string
  /** Value for `<html lang>` and `Content-Language`; a BCP 47 tag. */
  htmlLang: string
  /** For `Intl.DateTimeFormat` / `NumberFormat`. */
  intlLocale: string
  /** OpenGraph `og:locale`. */
  ogLocale: string
  dir: 'ltr' | 'rtl'
}

export const LOCALE_META: Record<Locale, LocaleMeta> = {
  mn: { label: 'Монгол',  htmlLang: 'mn',      intlLocale: 'mn-MN', ogLocale: 'mn_MN', dir: 'ltr' },
  en: { label: 'English', htmlLang: 'en',      intlLocale: 'en-US', ogLocale: 'en_US', dir: 'ltr' },
  ru: { label: 'Русский', htmlLang: 'ru',      intlLocale: 'ru-RU', ogLocale: 'ru_RU', dir: 'ltr' },
  es: { label: 'Español', htmlLang: 'es',      intlLocale: 'es-ES', ogLocale: 'es_ES', dir: 'ltr' },
}

export interface DomainPolicy {
  /** The language this domain opens in. */
  locale: Locale
  /**
   * Whether `Accept-Language` may override that default.
   *
   * Off for fuse.mn on purpose. Browsers in Mongolia ship with `en-US` far more
   * often than they ship with `mn`, so honouring the header there would show
   * English to the very audience the domain exists for. On the international
   * domains the header is the best signal available and is trusted.
   */
  negotiate: boolean
}

/**
 * What each domain implies about language.
 *
 * fuse.mn is the Mongolian storefront and opens in Mongolian, full stop.
 * fuse.page and fuse.contact are the international ones: English by default,
 * but a Russian- or Spanish-speaking visitor gets their own language from the
 * browser without having to find the switcher.
 *
 * This is only ever consulted third. A returning visitor's cookie and a
 * signed-in user's saved preference both outrank it, so someone who chose
 * Mongolian on fuse.page keeps Mongolian, and someone who chose English on
 * fuse.mn keeps English.
 */
export const DOMAIN_POLICY: Record<string, DomainPolicy> = {
  'fuse.mn': { locale: 'mn', negotiate: false },
  'fuse.page': { locale: 'en', negotiate: true },
  'fuse.contact': { locale: 'en', negotiate: true },
}

export function isLocale(value: unknown): value is Locale {
  return typeof value === 'string' && (LOCALES as readonly string[]).includes(value)
}

/**
 * Maps a Host header to its language policy.
 *
 * Matches on the registrable domain so that `www.fuse.mn`, `app.fuse.mn` and a
 * user subdomain all resolve the same way. Unknown hosts — localhost, preview
 * deployments, a domain added before this map is updated — return null so the
 * caller falls through to `Accept-Language` rather than silently guessing.
 */
export function policyForHost(host: string | null | undefined): DomainPolicy | null {
  if(!host) return null

  // Strip the port, lowercase, drop a trailing dot from an absolute FQDN.
  const hostname = host.split(':')[0].trim().toLowerCase().replace(/\.$/, '')
  if(!hostname) return null

  for(const [domain, policy] of Object.entries(DOMAIN_POLICY)) {
    if(hostname === domain || hostname.endsWith(`.${domain}`)) return policy
  }
  return null
}

/**
 * Picks the best supported language out of an `Accept-Language` header.
 *
 * Ranks by the q value the browser sent, then matches on the primary subtag, so
 * `es-419` and `es-MX` both land on `es`, and `ru-BY` on `ru`. Returns null when
 * the header names nothing we speak.
 */
export function localeFromAcceptLanguage(header: string | null | undefined): Locale | null {
  if(!header) return null

  const ranked = header
    .split(',')
    .map(part => {
      const [tag, ...params] = part.trim().split(';')
      const q = params
        .map(p => p.trim())
        .find(p => p.startsWith('q='))
      const quality = q ? Number.parseFloat(q.slice(2)) : 1
      return { tag: tag.trim().toLowerCase(), quality: Number.isNaN(quality) ? 0 : quality }
    })
    .filter(entry => entry.tag && entry.quality > 0)
    .sort((a, b) => b.quality - a.quality)

  for(const { tag } of ranked) {
    if(tag === '*') continue
    const primary = tag.split('-')[0]
    if(isLocale(primary)) return primary
  }
  return null
}
